/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { t } from 'i18next';
import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { placeholderBackup } from '@/assets';
import { global, GlobalComponentType } from '@/context';
import { baseStyles, buttonStyles, rootVariablesStyles } from '@/styles';
import { consume } from '@lit/context';

@customElement('fg-image')
export class FgImage extends LitElement {
  @property({ type: String })
  src!: string;

  @property({ type: String })
  alt = '';

  @property({ type: String })
  class?: string = '';

  @property({ type: String })
  width?: string;

  @property({ type: String })
  height?: string;

  /* if no global context is provided, use the this placeholder */
  @property({ type: String })
  placeholder?: string;

  @property({ type: Boolean })
  clickable?: boolean | null;

  @consume({ context: global.context, subscribe: true })
  globalContext!: GlobalComponentType;

  private handleClickEvent() {
    this.dispatchEvent(
      new CustomEvent('fg-image-click-event', {
        composed: true,
      }),
    );
  }

  private renderImage() {
    return html`
      <img
        part="image"
        src=${this.src}
        .class=${this.class ?? ''}
        alt=${this.alt}
        width=${this.width ?? ''}
        height=${this.height ?? ''}
        @error=${(e: Event) => {
          const img = e.currentTarget as HTMLImageElement;
          img.onerror = null;
          img.src =
            this.placeholder ||
            this.globalContext.placeholder ||
            placeholderBackup;
          img.classList.add('placeholder'); //not explicitly used but used for clearance purposes
        }}
      />
    `;
  }

  render() {
    return this.clickable
      ? html`
          <button class="button-image" @click=${this.handleClickEvent}>
            ${this.renderImage()}
            <span class="sr-only">${t('labels.openLargeImage')}</span>
          </button>
        `
      : html`
          ${this.renderImage()}
        `;
  }

  static styles = [
    baseStyles,
    rootVariablesStyles,
    buttonStyles,
    css`
      button.button-image:focus-visible {
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        outline: 2px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
      }
    `,
  ];
}
